/**
 * <EscalationDialog> — operator escalation modal (#237).
 *
 * Triggered by the "Page on-call" button at the top of the Workloads tab.
 * Captures optional notes and, on confirm, calls
 * `clusterApi.requestSupport(siteId, { notes })` which:
 *   1) emits a `deployment.support_request` Kafka event
 *      (consumed by the future on-call routing service), and
 *   2) co-emits a `deployment.audit` row (verb='escalate').
 *
 * The button itself + this modal are gated by `<RoleGate role={EventOperator}>`
 * — see WorkloadsPanel for the wrapper. Defense-in-depth: the BFF and
 * deployment-service mirror also enforce the same role.
 *
 * On success, surfaces a success state with the returned `referenceId`
 * (= event timestamp) so the operator can quote it to on-call. On
 * failure, shows the server error inline — does not auto-close so the
 * operator can retry without losing their notes.
 */
import { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { clusterApi } from '@/api/client';

interface Props {
  siteId: string;
  onClose: () => void;
  /** Called once the request succeeds, with the on-call reference id. */
  onSuccess?: (referenceId: string) => void;
}

export default function EscalationDialog({ siteId, onClose, onSuccess }: Props) {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referenceId, setReferenceId] = useState<string | null>(null);

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const result = await clusterApi.requestSupport(siteId, {
        notes: notes.trim() ? notes.trim() : undefined,
      });
      setReferenceId(result.referenceId);
      onSuccess?.(result.referenceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const isSuccess = referenceId !== null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label="Page on-call"
    >
      <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-md mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-status-error" />
            <h3 className="text-silver-bright font-semibold">
              {isSuccess ? 'On-call paged' : 'Page on-call'}
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Dismiss dialog"
            className="text-silver-muted hover:text-silver-bright transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {!isSuccess && (
            <>
              <p className="text-sm text-silver-muted">
                This will page the on-call engineer with a snapshot of this site&apos;s workloads
                and recent cluster events. Use only for issues that need immediate attention.
              </p>
              <div className="space-y-1.5">
                <label htmlFor="escalation-notes" className="text-xs text-silver-muted">
                  Notes (optional)
                </label>
                <textarea
                  id="escalation-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="What you were doing, what you saw, any pertinent context..."
                  className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder:text-silver-muted/30 focus:border-gold focus:outline-none resize-none"
                />
              </div>
              {error && (
                <div className="rounded-lg border border-status-error/30 bg-status-error/5 px-3 py-2 text-sm text-status-error">
                  {error}
                </div>
              )}
            </>
          )}

          {isSuccess && (
            <div className="space-y-3">
              <p className="text-sm text-silver-bright">
                On-call has been paged. Quote this reference if they ask:
              </p>
              <div className="bg-surface-deep/50 rounded-lg px-4 py-3">
                <div className="text-xs text-silver-muted">Reference ID</div>
                <div className="text-sm text-silver-bright font-mono break-all">{referenceId}</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          {!isSuccess ? (
            <>
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-status-error text-white hover:bg-status-error/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Paging...' : 'Page on-call'}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-gold text-surface-deep hover:bg-gold-light transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
